import * as THREE from 'three';
import { yieldToMain } from '@/core/scheduler';
import { whenIdle } from '@/core/idleGate';

/**
 * warmup.ts — GPU warm-up helpers for the race-start pipeline (loading round).
 *
 * three.js compiles a material's shader program and uploads its textures on
 * the FIRST frame that draws them. In a race that first frame is mid-drive
 * (a landmark enters view, a car model lands), and on a phone each compile is
 * a 20-100 ms main-thread stall — the "first race stutters, second race is
 * fine" signature, amplified online where a stall drops sim ticks. These
 * helpers do that work up front, under the loading splash:
 *   - collectSceneTextures: every texture any material in the scene references
 *   - initTexturesChunked: renderer.initTexture() in time-budgeted slices
 *   - compileSceneAsync: renderer.compileAsync (KHR_parallel_shader_compile)
 * The helpers are renderer/scene-generic; Game.warmUpRace sequences them.
 */

const TEXTURE_KEYS = [
  'map',
  'normalMap',
  'roughnessMap',
  'metalnessMap',
  'emissiveMap',
  'bumpMap',
  'aoMap',
  'alphaMap',
  'envMap',
  'specularMap',
  'displacementMap',
  'lightMap',
  'clearcoatMap',
  'clearcoatNormalMap',
  'clearcoatRoughnessMap',
  'sheenColorMap',
  'transmissionMap',
  'thicknessMap',
] as const;

/** Every distinct texture referenced by any mesh material in `root`. */
export function collectSceneTextures(root: THREE.Object3D): THREE.Texture[] {
  const out = new Set<THREE.Texture>();
  const visit = (mat: THREE.Material): void => {
    const m = mat as unknown as Record<string, unknown>;
    for (const k of TEXTURE_KEYS) {
      const t = m[k];
      if (t && (t as THREE.Texture).isTexture) out.add(t as THREE.Texture);
    }
  };
  root.traverse((o) => {
    const mesh = o as THREE.Mesh;
    if (!mesh.isMesh && !(o as THREE.Sprite).isSprite && !(o as THREE.Points).isPoints) return;
    const mat = mesh.material;
    if (!mat) return;
    if (Array.isArray(mat)) mat.forEach(visit);
    else visit(mat);
  });
  return Array.from(out);
}

/**
 * Upload textures to the GPU ahead of their first draw, in slices of at most
 * `budgetMs` main-thread time (a 2K upload can take 10-30 ms on a phone; a
 * city has dozens). `onStep(done, total)` feeds the progress bar.
 */
export async function initTexturesChunked(
  renderer: THREE.WebGLRenderer,
  textures: THREE.Texture[],
  budgetMs = 8,
  onStep?: (done: number, total: number) => void,
  cancelled?: () => boolean,
): Promise<void> {
  let sliceStart = performance.now();
  for (let i = 0; i < textures.length; i++) {
    if (cancelled?.()) return;
    const t = textures[i];
    try {
      // Render-target textures + compressed textures without data are skipped
      // by initTexture itself; a texture whose image never decoded just no-ops.
      if (t.image || t.isRenderTargetTexture) renderer.initTexture(t);
    } catch {
      /* a bad texture must never abort the warm-up */
    }
    onStep?.(i + 1, textures.length);
    if (performance.now() - sliceStart >= budgetMs && i < textures.length - 1) {
      await yieldToMain();
      sliceStart = performance.now();
    }
  }
}

/** Precompile every material's program; resolves when the GPU reports them
 *  linked (parallel-compile extension) or after `capMs`, whichever is first —
 *  a driver without the extension would otherwise block the splash forever. */
export function compileSceneAsync(
  renderer: THREE.WebGLRenderer,
  scene: THREE.Scene,
  camera: THREE.Camera,
  capMs = 6000,
): Promise<'ready' | 'timeout' | 'error'> {
  return new Promise((resolve) => {
    let done = false;
    const timer = setTimeout(() => {
      if (!done) {
        done = true;
        resolve('timeout');
      }
    }, capMs);
    let p: Promise<unknown>;
    try {
      p = renderer.compileAsync(scene, camera);
    } catch {
      clearTimeout(timer);
      done = true;
      resolve('error');
      return;
    }
    p.then(
      () => {
        if (!done) {
          done = true;
          clearTimeout(timer);
          resolve('ready');
        }
      },
      () => {
        if (!done) {
          done = true;
          clearTimeout(timer);
          resolve('error');
        }
      },
    );
  });
}

/**
 * Loading round 2 — CHUNKED shader precompile. `renderer.compileAsync(scene)`
 * issues every program in ONE synchronous pass, and on ANGLE-Metal (Chrome
 * on Mac, WKWebView on iOS) a cold-cache link is expensive on the calling
 * thread: MEASURED 4.1 s for the NYC world's 102 programs on an M4 Pro. A
 * tap landing behind that is the owner's "delay, then it jumps". This walks
 * the scene's top-level objects (track mesh, each scenery family, each car,
 * each landmark) and compiles one subtree at a time, yielding — and, when
 * `gated`, waiting for the player to be quiet — between them, so no single
 * task holds the thread for more than one subtree's programs.
 * Resolves 'ready' when every program reports linked, 'timeout' at `capMs`.
 */
export async function compileSceneChunked(
  renderer: THREE.WebGLRenderer,
  scene: THREE.Scene,
  camera: THREE.Camera,
  opts: {
    gated?: boolean;
    cancelled?: () => boolean;
    onProgress?: (done: number, total: number) => void;
    capMs?: number;
  } = {},
): Promise<'ready' | 'timeout' | 'cancelled' | 'error'> {
  // Compile units: the scene's direct children, but flatten the big
  // container groups (scenery, landmarks) one level so a family is a unit.
  const units: THREE.Object3D[] = [];
  for (const child of scene.children) {
    const kids = child.children;
    if (child.type === 'Group' && kids.length > 4) {
      for (const k of kids) units.push(k);
    } else {
      units.push(child);
    }
  }
  const deadline = performance.now() + (opts.capMs ?? 15000);
  let status: 'ready' | 'timeout' | 'cancelled' | 'error' = 'ready';
  for (let i = 0; i < units.length; i++) {
    if (opts.cancelled?.()) return 'cancelled';
    if (performance.now() > deadline) return 'timeout';
    if (opts.gated) await whenIdle(300, 3000);
    else await yieldToMain();
    if (opts.cancelled?.()) return 'cancelled';
    const u = units[i];
    try {
      // compileAsync(object, camera, targetScene): materials from the object
      // subtree, lights from the target scene — the exact programs the real
      // draw will use (instancing / vertex colours come from the real meshes).
      const p = renderer.compileAsync(u, camera, scene);
      const result = await Promise.race([
        p.then(() => 'ok' as const),
        new Promise<'slow'>((r) => setTimeout(() => r('slow'), 4000)),
      ]);
      if (result === 'slow') status = 'timeout';
    } catch {
      status = 'error';
    }
    opts.onProgress?.(i + 1, units.length);
  }
  return status;
}
